import { BaseProduct } from "@CommonTypes/merchflow/pog/product";
import { useSetAtom } from "jotai";
import { useEffect } from "react";
import {
  Flex,
  Select,
  Button,
  HorizontalDivider,
  SmallNumberInput,
  Color,
  Text,
} from "src/elements";
import { useNewCdtNodeModal } from "src/modals/NewCdtNode/store/hooks";
import { useTranslation } from "src/i18n";
import { merchflows_merchflowId_BasePog_basePogId_globalChangesAtom } from "src/pages/Merchflows_merchflowId_BasePog_basePogId_/store/atoms";

type CdtLevel = "variant" | "cdt1" | "cdt2" | "cdt3";
type CdtPriority = "cdt1Priority" | "cdt2Priority" | "cdt3Priority";

export type CdtEditorProduct = Pick<
  BaseProduct,
  "variant" | "cdt1" | "cdt1Priority" | "cdt2" | "cdt2Priority" | "cdt3" | "cdt3Priority"
>;

const OPTION_MIXED = "OPTION_MIXED";

interface Props {
  selectedItems: CdtEditorProduct[];
  cdtSourceItems: CdtEditorProduct[];
  saveChanges: () => void;
}

export const ItemCdtEditor = ({ selectedItems, cdtSourceItems, saveChanges }: Props) => {
  const { t } = useTranslation("common");
  const { openNewCdtNodeModal, newCdtNodeData } = useNewCdtNodeModal();
  const setGlobalChanges = useSetAtom(merchflows_merchflowId_BasePog_basePogId_globalChangesAtom);

  if (selectedItems.length === 0) {
    return null;
  }

  const getCdtLevel = (cdtLevel: CdtLevel) => {
    const cdt = selectedItems[0][cdtLevel];

    for (const selectedItem of selectedItems) {
      if (selectedItem[cdtLevel] !== cdt) {
        return OPTION_MIXED;
      }
    }

    return cdt;
  };

  const cdtVariant = getCdtLevel("variant");
  const cdt1 = getCdtLevel("cdt1");
  const cdt2 = getCdtLevel("cdt2");
  const cdt3 = getCdtLevel("cdt3");

  // Effect to read cdt value from the modal and save it to selected items.
  useEffect(() => {
    if (newCdtNodeData && newCdtNodeData.value !== null) {
      for (const selectedItem of selectedItems) {
        selectedItem[newCdtNodeData.cdtLevel] = newCdtNodeData.value;
      }
      saveChanges();
      setGlobalChanges(true);
    }
  }, [newCdtNodeData]);

  const getCdts = (cdtLevel: CdtLevel) => {
    const cdts: { [key: string]: boolean } = {};

    for (const item of cdtSourceItems) {
      cdts[item[cdtLevel]] = true;
    }

    const cdtsArray: { label: string; value: string; isDisabled?: boolean }[] = Object.keys(cdts)
      .sort()
      .map((cdt) => ({ label: cdt, value: cdt }));

    if (getCdtLevel(cdtLevel) === OPTION_MIXED) {
      cdtsArray.push({ label: "MIXED VALUES", value: OPTION_MIXED, isDisabled: true });
    }

    return cdtsArray;
  };

  const setCdtLevel = (cdtLevel: CdtLevel, value: string) => {
    for (const selectedItem of selectedItems) {
      selectedItem[cdtLevel] = value;
    }
    saveChanges();
    setGlobalChanges(true);
  };

  const getCdtPriority = (cdtPriority: CdtPriority) => {
    let maxCdtPriority = 0;

    for (const selectedItem of selectedItems) {
      maxCdtPriority = Math.max(maxCdtPriority, selectedItem[cdtPriority]);
    }

    return maxCdtPriority;
  };

  const setCdtPriority = (cdtPriority: CdtPriority, value: number) => {
    for (const selectedItem of selectedItems) {
      selectedItem[cdtPriority] = value;
    }

    saveChanges();
    setGlobalChanges(true);
  };

  return (
    <Flex column gap="10px">
      <Text variant="small1" color={Color.primary}>
        {t("planogram.assortment")}
      </Text>

      <Flex gap="10px">
        <Select
          width="256px"
          title="Variant"
          isSearchable
          options={getCdts("variant")}
          value={cdtVariant}
          setValue={(value) => setCdtLevel("variant", value)}
        />
        <Flex minWidth="35px">
          <Button
            color="greenSmoke"
            width="35px"
            iconLeft={{ name: "plus" }}
            onClick={() => openNewCdtNodeModal("variant")}
          />
        </Flex>
      </Flex>

      <HorizontalDivider />

      <Flex gap="10px" padding="10px 0 0 0">
        <Select
          width="256px"
          title="CDT-1"
          isSearchable
          options={getCdts("cdt1")}
          value={cdt1}
          setValue={(value) => setCdtLevel("cdt1", value)}
        />
        <Flex minWidth="35px">
          <Button
            color="greenSmoke"
            width="35px"
            iconLeft={{ name: "plus" }}
            onClick={() => openNewCdtNodeModal("cdt1")}
          />
        </Flex>
      </Flex>

      <SmallNumberInput
        label="CDT-1 Priority:"
        variant="sidebuttons"
        textVariant="caption1"
        textColor={Color.primaryActive}
        width="20px"
        value={getCdtPriority("cdt1Priority")}
        setValue={(cdtPriority) => setCdtPriority("cdt1Priority", cdtPriority)}
        min={0}
      />

      <HorizontalDivider />

      <Flex gap="10px" padding="10px 0 0 0">
        <Select
          width="256px"
          title="CDT-2"
          isSearchable
          options={getCdts("cdt2")}
          value={cdt2}
          setValue={(value) => setCdtLevel("cdt2", value)}
        />

        <Flex minWidth="35px">
          <Button
            color="greenSmoke"
            width="35px"
            iconLeft={{ name: "plus" }}
            onClick={() => openNewCdtNodeModal("cdt2")}
          />
        </Flex>
      </Flex>

      <SmallNumberInput
        label="CDT-2 Priority:"
        variant="sidebuttons"
        textVariant="caption1"
        textColor={Color.primaryActive}
        width="20px"
        value={getCdtPriority("cdt2Priority")}
        setValue={(cdtPriority) => setCdtPriority("cdt2Priority", cdtPriority)}
        min={0}
      />

      <HorizontalDivider />

      <Flex gap="10px" padding="10px 0 0 0">
        <Select
          width="256px"
          title="CDT-3"
          isSearchable
          options={getCdts("cdt3")}
          value={cdt3}
          setValue={(value) => setCdtLevel("cdt3", value)}
        />

        <Flex minWidth="35px">
          <Button
            color="greenSmoke"
            width="35px"
            iconLeft={{ name: "plus" }}
            onClick={() => openNewCdtNodeModal("cdt3")}
          />
        </Flex>
      </Flex>

      <SmallNumberInput
        label="CDT-3 Priority:"
        variant="sidebuttons"
        textVariant="caption1"
        textColor={Color.primaryActive}
        width="20px"
        value={getCdtPriority("cdt3Priority")}
        setValue={(cdtPriority) => setCdtPriority("cdt3Priority", cdtPriority)}
        min={0}
      />
    </Flex>
  );
};
